import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Suspense, lazy } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AdminLayout } from "@/components/AdminLayout";
import { queryClient } from "@/lib/queryClient";
import {
  LandingSkeleton,
  QuestionnaireSkeleton,
  ResultsSkeleton,
  MagazinSkeleton,
  ArticleSkeleton,
  PageSkeleton,
  ProductsSkeleton,
  AdminSkeleton,
  SimpleSkeleton,
} from "@/components/PageSkeletons";

// Lazy imports for code-splitting
const Landing = lazy(() => import("./pages/Landing"));
const Questionnaire = lazy(() => import("./pages/Questionnaire"));
const Results = lazy(() => import("./pages/Results"));
const Liked = lazy(() => import("./pages/Liked"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const Magazin = lazy(() => import("./pages/Magazin"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const SEOLandingPage = lazy(() => import("./pages/SEOLandingPage"));
const GeschenkideenHub = lazy(() => import("./pages/GeschenkideenHub"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminTrendFinder = lazy(() => import("./pages/admin/TrendFinder"));
const AdminSEOAnalyzer = lazy(() => import("./pages/admin/SEOAnalyzer"));
const AdminArticleGenerator = lazy(() => import("./pages/admin/ArticleGenerator"));
const AdminCronJobs = lazy(() => import("./pages/admin/CronJobs"));
const AdminArticleBackups = lazy(() => import("./pages/admin/ArticleBackups"));
const AdminAPITester = lazy(() => import("./pages/admin/APITester"));
const AdminArticleOptimizer = lazy(() => import("./pages/admin/ArticleOptimizer"));
const AdminSEOPages = lazy(() => import("./pages/admin/SEOPages"));
const AdminTrendProductPages = lazy(() => import("./pages/admin/TrendProductPages"));
const AdminGiftAnalytics = lazy(() => import("./pages/admin/GiftAssistantAnalytics"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const UeberUns = lazy(() => import("./pages/UeberUns"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const ProductsHub = lazy(() => import("./pages/ProductsHub"));
const ProductCategory = lazy(() => import("./pages/ProductCategory"));
const ProductPage = lazy(() => import("./pages/ProductPage"));

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
          <Toaster />
          <Sonner />
          <div className="overflow-x-hidden w-full">
            <ScrollToTop />
            <Header />
            <Routes>
              {/* Public Routes with specific skeletons */}
              <Route path="/" element={
                <Suspense fallback={<LandingSkeleton />}>
                  <Landing />
                </Suspense>
              } />
              <Route path="/questionnaire" element={
                <Suspense fallback={<QuestionnaireSkeleton />}>
                  <Questionnaire />
                </Suspense>
              } />
              <Route path="/results" element={
                <Suspense fallback={<ResultsSkeleton />}>
                  <Results />
                </Suspense>
              } />
              <Route path="/liked" element={
                <Suspense fallback={<PageSkeleton />}>
                  <Liked />
                </Suspense>
              } />
              <Route path="/magazin" element={
                <Suspense fallback={<MagazinSkeleton />}>
                  <Magazin />
                </Suspense>
              } />
              <Route path="/magazin/:slug" element={
                <Suspense fallback={<ArticleSkeleton />}>
                  <ArticlePage />
                </Suspense>
              } />
              
              {/* Geschenkideen Hub Page */}
              <Route path="/geschenkideen" element={
                <Suspense fallback={<PageSkeleton />}>
                  <GeschenkideenHub />
                </Suspense>
              } />
              
              {/* Product SEO Pages */}
              <Route path="/produkte" element={
                <Suspense fallback={<ProductsSkeleton />}>
                  <ProductsHub />
                </Suspense>
              } />
              <Route path="/produkte/:kategorie" element={
                <Suspense fallback={<ProductsSkeleton />}>
                  <ProductCategory />
                </Suspense>
              } />
              <Route path="/produkte/:kategorie/:slug" element={
                <Suspense fallback={<ArticleSkeleton />}>
                  <ProductPage />
                </Suspense>
              } />
              
              {/* Programmatic SEO Landing Pages */}
              <Route path="/geschenkideen/*" element={
                <Suspense fallback={<PageSkeleton />}>
                  <SEOLandingPage />
                </Suspense>
              } />
              <Route path="/geschenke-fuer-:slug" element={
                <Suspense fallback={<PageSkeleton />}>
                  <SEOLandingPage />
                </Suspense>
              } />
              <Route path="/geschenke-:slug" element={
                <Suspense fallback={<PageSkeleton />}>
                  <SEOLandingPage />
                </Suspense>
              } />
              <Route path="/:slug" element={
                <Suspense fallback={<PageSkeleton />}>
                  <SEOLandingPage />
                </Suspense>
              } />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={
                <Suspense fallback={<SimpleSkeleton />}>
                  <AdminLogin />
                </Suspense>
              } />
              
              <Route path="/admin" element={
                <Suspense fallback={<AdminSkeleton />}>
                  <AdminLayout />
                </Suspense>
              }>
                <Route index element={
                  <Suspense fallback={<AdminSkeleton />}>
                    <AdminDashboard />
                  </Suspense>
                } />
                <Route path="dashboard" element={
                  <Suspense fallback={<AdminSkeleton />}>
                    <AdminDashboard />
                  </Suspense>
                } />
                <Route path="trends" element={
                  <Suspense fallback={<AdminSkeleton />}>
                    <AdminTrendFinder />
                  </Suspense>
                } />
                <Route path="seo" element={
                  <Suspense fallback={<AdminSkeleton />}>
                    <AdminSEOAnalyzer />
                  </Suspense>
                } />
                <Route path="seo-pages" element={
                  <Suspense fallback={<AdminSkeleton />}>
                    <AdminSEOPages />
                  </Suspense>
                } />
                <Route path="product-trends" element={
                  <Suspense fallback={<AdminSkeleton />}>
                    <AdminTrendProductPages />
                  </Suspense>
                } />
                <Route path="generator" element={
                  <Suspense fallback={<AdminSkeleton />}>
                    <AdminArticleGenerator />
                  </Suspense>
                } />
                <Route path="jobs" element={
                  <Suspense fallback={<AdminSkeleton />}>
                    <AdminCronJobs />
                  </Suspense>
                } />
                <Route path="backups" element={
                  <Suspense fallback={<AdminSkeleton />}>
                    <AdminArticleBackups />
                  </Suspense>
                } />
                <Route path="apis" element={
                  <Suspense fallback={<AdminSkeleton />}>
                    <AdminAPITester />
                  </Suspense>
                } />
                <Route path="optimizer" element={
                  <Suspense fallback={<AdminSkeleton />}>
                    <AdminArticleOptimizer />
                  </Suspense>
                } />
                <Route path="gift-analytics" element={
                  <Suspense fallback={<AdminSkeleton />}>
                    <AdminGiftAnalytics />
                  </Suspense>
                } />
              </Route>
              
              {/* Legal/Static Pages */}
              <Route path="/impressum" element={
                <Suspense fallback={<SimpleSkeleton />}>
                  <Impressum />
                </Suspense>
              } />
              <Route path="/datenschutz" element={
                <Suspense fallback={<SimpleSkeleton />}>
                  <Datenschutz />
                </Suspense>
              } />
              <Route path="/ueber-uns" element={
                <Suspense fallback={<SimpleSkeleton />}>
                  <UeberUns />
                </Suspense>
              } />
              <Route path="/kontakt" element={
                <Suspense fallback={<SimpleSkeleton />}>
                  <Kontakt />
                </Suspense>
              } />
              
              {/* Catch-all */}
              <Route path="*" element={
                <Suspense fallback={<SimpleSkeleton />}>
                  <NotFound />
                </Suspense>
              } />
            </Routes>
            <Footer />
          </div>
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
